import { z } from 'zod';
import { getFirstMessage, getFirstMessageMode } from '../generate-system-prompt';
import { evalTestCaseToTurns, isKnowledgeBaseLookup, turnsToEvalTestCase } from '../test-cases';
import { optionalAgentIdSchema } from './agent-tool-schemas';
// An agent's test cases: a saved conversation plus what the agent should do on its next turn. Via builds the
// conversation by playing the customer one turn at a time, so every agent turn in it is one the agent really produced.
const MAX_TURN_CHARS = 1500;
export const DEFAULT_TEST_SAMPLES = 3;
export const MAX_TEST_SAMPLES = 5;
export const DEFAULT_TEST_THRESHOLD = 0.7;
const MAX_SHOWN_FAILURES = 2;
export const MAX_TESTS_PER_SAVE = 25;
const shorten = (text) => (text.length > MAX_TURN_CHARS ? `${text.slice(0, MAX_TURN_CHARS)}…` : text);
const actionCallSchema = z.object({
    toolCallId: z.string(),
    name: z.string().describe("The action's tool name"),
    arguments: z.string().describe('The arguments the agent passed, as JSON')
});
const turnSchema = z.discriminatedUnion('role', [
    z.object({ role: z.literal('customer'), message: z.string() }),
    z.object({ role: z.literal('agent'), message: z.string() }),
    actionCallSchema.extend({
        role: z.literal('action'),
        result: z.string().optional().describe('What the action returned to the agent in this conversation')
    })
]);
const expectsSchema = z.union([
    z.object({
        reply: z
            .string()
            .min(1)
            .describe('What the agent should say on its next turn. Graded on meaning, so write the facts and the next step it must contain, not exact wording.')
    }),
    z.object({
        action: z.string().min(1).describe('The tool name of the action the agent should call on its next turn'),
        arguments: z
            .string()
            .optional()
            .describe('The arguments it should pass, as a JSON object. Ids, numbers and dates must match exactly; free text is graded on meaning. Defaults to {}.')
    })
]);
const testCaseIdSchema = z.string().describe('From list_test_cases');
export const listTestCasesToolSchema = {
    name: 'list_test_cases',
    description: "List an agent's test cases: each one's name, how long its conversation is, and what it expects from the agent's next turn.",
    inputSchema: z.object({ agentId: optionalAgentIdSchema }),
    outputSchema: z.object({
        agentId: z.string(),
        testCases: z.array(z.object({
            testCaseId: z.string(),
            name: z.string(),
            turns: z.number().describe('Turns in the saved conversation'),
            expects: expectsSchema.optional().describe('Missing when the test was saved without an expectation')
        }))
    })
};
export const getTestCaseToolSchema = {
    name: 'get_test_case',
    description: 'Read one test case: the saved conversation and what the agent is expected to do next.',
    inputSchema: z.object({ agentId: optionalAgentIdSchema, testCaseId: testCaseIdSchema }),
    outputSchema: z.object({
        testCaseId: z.string(),
        name: z.string(),
        conversation: z.array(turnSchema),
        expects: expectsSchema.optional()
    })
};
export const sendTestMessageToolSchema = {
    name: 'send_test_message',
    description: "Play the customer for one turn of a test conversation and get the agent's real next turn from its saved draft. Actions are not run: when the agent calls one, the turn ends there and you supply what the action returned with actionResults. You never write the agent's turns. Nothing is saved until save_test_cases.",
    inputSchema: z.object({
        agentId: optionalAgentIdSchema,
        conversationId: z
            .string()
            .optional()
            .describe("Continue this conversation. Leave out to start a new one, which opens with the agent's own opening message when it speaks first."),
        fromTestCaseId: z
            .string()
            .optional()
            .describe("Start a new conversation from this test case's saved conversation, to extend it. The agent takes the turn the test checks, and that turn comes back. Leave customerMessage and actionResults out of this call; send the customer's next move in the next one, with the conversationId."),
        customerMessage: z
            .string()
            .optional()
            .describe('What the customer says next, in their words. One turn only. Leave out when the agent is waiting for action results, or when starting from a test case.'),
        actionResults: z
            .array(z.object({
            toolCallId: z.string(),
            result: z
                .string()
                .describe('What the action returns, as the agent would receive it: copy the shape from a real run of the action, never invent fields')
        }))
            .optional()
            .describe('One result for every action the agent called on its last turn')
    }),
    outputSchema: z.object({
        conversationId: z.string(),
        agent: z.object({
            reply: z.string().optional().describe('What the agent said this turn'),
            actions: z
                .array(actionCallSchema)
                .describe('Actions the agent called this turn, still waiting for results')
        }),
        turns: z.number().describe('Turns in the conversation so far, including this one')
    })
};
const newTestCaseSchema = z.object({
    conversationId: z.string().describe('From send_test_message'),
    name: z
        .string()
        .min(1)
        .max(120)
        .describe('What the test protects, such as "Refuses claim details before verifying identity"'),
    expects: expectsSchema
});
export const saveTestCasesToolSchema = {
    name: 'save_test_cases',
    description: "Save test conversations as test cases, every one that is ready in the same call: the user reviews them together on one card and can leave some out. Each conversation is saved up to the agent's last turn, and that last turn is what its test checks from then on.",
    inputSchema: z.object({
        tests: z.array(newTestCaseSchema).min(1).max(MAX_TESTS_PER_SAVE).describe('One entry per conversation')
    }),
    outputSchema: z.object({
        saved: z.array(z.object({ testCaseId: z.string(), name: z.string() })),
        leftOut: z.array(z.string()).describe('Names of the tests the user chose not to save'),
        failed: z
            .array(z.object({ name: z.string(), error: z.string() }))
            .describe("Tests that couldn't be saved")
    })
};
export const updateTestCaseToolSchema = {
    name: 'update_test_case',
    description: "Rename a test case or change what it expects, when the agent's new behavior is the intended one. The saved conversation stays as it is.",
    inputSchema: z.object({
        agentId: optionalAgentIdSchema,
        testCaseId: testCaseIdSchema,
        name: z.string().min(1).max(120).optional(),
        expects: expectsSchema.optional(),
        summary: z.string().describe('Why the test is changing, shown to the user')
    }),
    outputSchema: z.object({ testCaseId: z.string(), name: z.string() })
};
export const deleteTestCaseToolSchema = {
    name: 'delete_test_case',
    description: 'Delete a test case.',
    inputSchema: z.object({ agentId: optionalAgentIdSchema, testCaseId: testCaseIdSchema }),
    outputSchema: z.object({ testCaseId: z.string(), name: z.string() })
};
const runSchema = z.object({
    score: z.number().describe('0 to 1'),
    reason: z.string().optional().describe('Why the run scored below 1'),
    agentDid: z.string().describe('What the agent said, or the action it called with its arguments')
});
export const checkAgentTestsToolSchema = {
    name: 'check_agent_tests',
    description: 'Check an agent against its test cases, as the last step of every build and every change, once the draft is saved: whether it has any, whether they still pass, and which of its actions no test covers. Each test runs several times against the saved draft and passes when every run scores at or above the threshold. Actions are not run and nothing is changed.',
    inputSchema: z.object({
        agentId: optionalAgentIdSchema,
        testCaseIds: z
            .array(z.string())
            .optional()
            .describe('Run only these, such as the ones that failed, after a fix. Leave out to check every test case.'),
        samples: z
            .number()
            .int()
            .min(1)
            .max(MAX_TEST_SAMPLES)
            .optional()
            .describe(`Runs per test, ${DEFAULT_TEST_SAMPLES} by default. One run says little about reliability.`)
    }),
    outputSchema: z.object({
        agentId: z.string(),
        samples: z.number(),
        threshold: z.number(),
        testCases: z.number().describe('How many test cases the agent has. With none, nothing protects it'),
        passed: z.number(),
        failed: z.number(),
        results: z.array(z.object({
            testCaseId: z.string(),
            name: z.string(),
            passed: z.boolean(),
            passedRuns: z.number(),
            runs: z.number(),
            expects: expectsSchema.optional(),
            failures: z.array(runSchema).describe(`Up to ${MAX_SHOWN_FAILURES} of the runs that failed`),
            error: z.string().optional().describe("Why the test couldn't run")
        })),
        untestedActions: z
            .array(z.string())
            .optional()
            .describe("The agent's actions that no test case expects it to call or has it calling. Left out when only some tests were run.")
    })
};
const toViaTurn = (turn) => {
    if (turn.role === 'tool') {
        return {
            role: 'action',
            toolCallId: turn.id,
            name: turn.name,
            arguments: turn.arguments,
            ...(turn.result !== undefined && { result: shorten(turn.result) })
        };
    }
    return { role: turn.role === 'bot' ? 'agent' : 'customer', message: shorten(turn.message) };
};
export const describeTestCaseExpectation = ({ expectedOutput, expectedToolName, expectedArguments }) => {
    if (expectedOutput)
        return { reply: expectedOutput };
    if (expectedToolName)
        return { action: expectedToolName, arguments: expectedArguments || '{}' };
    return undefined;
};
export const describeTestCases = (agentId, testCases) => ({
    agentId,
    testCases: testCases.map((testCase) => ({
        testCaseId: testCase.testCaseId,
        name: testCase.name,
        turns: evalTestCaseToTurns(testCase.testCase).length,
        expects: describeTestCaseExpectation(testCase)
    }))
});
export const describeTestCase = (testCase) => ({
    testCaseId: testCase.testCaseId,
    name: testCase.name,
    conversation: evalTestCaseToTurns(testCase.testCase).map(toViaTurn),
    expects: describeTestCaseExpectation(testCase)
});
/** The request fields the app's create and update endpoints take for an expectation. */
export const testCaseExpectationFields = (expects) => 'reply' in expects
    ? { expectedOutput: expects.reply }
    : { expectedToolName: expects.action, expectedArguments: expects.arguments?.trim() || '{}' };
const lastIndexWhere = (items, matches) => items.reduce((last, item, index) => (matches(item) ? index : last), -1);
const waitingActionCalls = (turns) => {
    const lastSpoken = lastIndexWhere(turns, (turn) => turn.role !== 'tool');
    return turns
        .slice(lastSpoken + 1)
        .flatMap((turn) => (turn.role === 'tool' && turn.result === undefined ? [turn] : []));
};
/**
 * The conversation with the customer's next move added: a message, or the results of the actions the agent is
 * waiting on. Throws, in words written for the model, when the move doesn't fit where the conversation stands.
 */
export const addCustomerMove = (conversation, { customerMessage, actionResults }) => {
    const waiting = waitingActionCalls(conversation.turns);
    if (waiting.length) {
        if (customerMessage) {
            throw new Error(`The agent is waiting for the result of ${waiting.map((turn) => turn.name).join(', ')}. Send actionResults for it first, without a customerMessage.`);
        }
        const results = new Map((actionResults ?? []).map(({ toolCallId, result }) => [toolCallId, result]));
        const missing = waiting.filter((turn) => !results.has(turn.id));
        if (missing.length) {
            throw new Error(`Send a result for every action the agent called: ${missing.map((turn) => `${turn.name} (${turn.id})`).join(', ')}.`);
        }
        return {
            ...conversation,
            turns: conversation.turns.map((turn) => turn.role === 'tool' && turn.result === undefined && results.has(turn.id)
                ? { ...turn, result: results.get(turn.id) }
                : turn)
        };
    }
    if (actionResults?.length)
        throw new Error('The agent is not waiting for any action result. Send a customerMessage.');
    if (!customerMessage?.trim())
        throw new Error('Send what the customer says next as customerMessage.');
    return {
        ...conversation,
        turns: [...conversation.turns, { role: 'user', message: customerMessage.trim() }]
    };
};
/**
 * A conversation picked up from a saved test. The saved conversation stops before the turn the test checks, so the
 * agent takes that turn before the customer makes another move. Throws, in words written for the model, when the call
 * also carries a move.
 */
export const testCaseToExtend = (agentId, testCase, { customerMessage, actionResults }) => {
    if (customerMessage || actionResults?.length) {
        throw new Error("Start from the test case on its own, without customerMessage or actionResults: the agent first takes the turn the test checks. Then send the customer's next move with the conversationId.");
    }
    return { agentId, turns: evalTestCaseToTurns(testCase) };
};
/** What the agent did on its turn, taken from the messages the app's endpoint returned for it. */
export const describeAgentTurn = (agentMessages) => {
    const turns = evalTestCaseToTurns(agentMessages);
    const reply = turns.flatMap((turn) => (turn.role === 'bot' ? [turn.message] : [])).join('\n\n');
    return {
        ...(reply && { reply: shorten(reply) }),
        actions: waitingActionCalls(turns).map((turn) => ({
            toolCallId: turn.id,
            name: turn.name,
            arguments: turn.arguments
        }))
    };
};
export const addAgentTurn = (conversation, agentMessages) => ({
    ...conversation,
    turns: [...conversation.turns, ...evalTestCaseToTurns(agentMessages)]
});
/**
 * The conversation a test case saves: everything before the agent's last turn, which is the turn the test checks.
 * Knowledge base lookups the agent made on that turn go with it.
 */
export const testCaseConversation = ({ turns }) => {
    const lastCustomerMove = lastIndexWhere(turns, (turn) => turn.role === 'user' ||
        (turn.role === 'tool' && turn.result !== undefined && !isKnowledgeBaseLookup(turn.name, turn.metadata)));
    if (lastCustomerMove === -1 || lastCustomerMove === turns.length - 1) {
        throw new Error("The conversation has no agent turn to check yet. Send the customer's message first.");
    }
    return turnsToEvalTestCase(turns.slice(0, lastCustomerMove + 1));
};
export const describeTestCaseRun = (testCase, runs, threshold) => {
    const failures = runs.filter((run) => (run.similarityScore ?? 0) < threshold);
    return {
        testCaseId: testCase.testCaseId,
        name: testCase.name,
        passed: runs.length > 0 && failures.length === 0,
        passedRuns: runs.length - failures.length,
        runs: runs.length,
        expects: describeTestCaseExpectation(testCase),
        failures: failures.slice(0, MAX_SHOWN_FAILURES).map((run) => ({
            score: run.similarityScore ?? 0,
            ...(run.reason && { reason: run.reason }),
            agentDid: shorten(run.assistantResponse)
        }))
    };
};
/** The agent's actions that no test covers: none expects the call, and none has the agent calling it on the way. */
export const untestedActions = (actionNames, testCases) => {
    const tested = new Set(testCases.flatMap(({ expectedToolName, testCase }) => [
        ...(expectedToolName ? [expectedToolName] : []),
        ...evalTestCaseToTurns(testCase).flatMap((turn) => (turn.role === 'tool' ? [turn.name] : []))
    ]));
    return actionNames.filter((name) => !tested.has(name));
};
export const describeTestCheck = (agentId, { samples, threshold }, results, 
/** How many test cases the agent has, and its untested actions when every test was run. */
agent) => ({
    agentId,
    samples,
    threshold,
    testCases: agent.testCases,
    passed: results.filter((result) => result.passed).length,
    failed: results.filter((result) => !result.passed).length,
    results,
    ...(agent.untestedActions && { untestedActions: agent.untestedActions })
});
export const UNSAVED_AGENT_CHANGES_ERROR = 'The agent has unsaved changes, and tests run its saved draft. Save the draft first, or ask the user to click Save draft.';
/** A test's agent turns must all come from one saved draft, or the test describes an agent that never existed. */
export const requireSameAgentVersion = (conversation, agentVersionId) => {
    if (conversation.agentVersionId && agentVersionId && conversation.agentVersionId !== agentVersionId) {
        throw new Error("The agent's draft was saved again since this conversation started, so its earlier turns came from an older draft. Start a new conversation.");
    }
};
export const missingTestConversationError = (conversationId) => new Error(`There is no test conversation "${conversationId}". Conversations are lost when the page reloads; start a new one.`);
/** How a new test conversation starts: with the agent's own opening line when it speaks first on an inbound call. */
export const testConversationOpening = (prompt) => {
    const opening = getFirstMessage(prompt, 'call', 'inbound').trim();
    return opening && getFirstMessageMode(prompt, 'inbound') !== 'assistant-waits-for-user'
        ? [{ role: 'bot', message: opening }]
        : [];
};
//# sourceMappingURL=test-cases.js.map